import { useState } from "react";
import { PencilIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type EditDialogProps = {
  title: string;
  description: string;
  initialValue: number | null | undefined;
  onSave: (newValue: number) => void;
  label: string;
  unit?: string;
  type?: string;
};

export function EditDialog({
  title,
  description,
  initialValue,
  onSave,
  label,
  unit,
  type,
}: EditDialogProps) {
  const [newValue, setNewValue] = useState(
    initialValue !== null && initialValue !== undefined
      ? String(initialValue)
      : ""
  );
  const [isOpen, setIsOpen] = useState(false);

  const handleSave = () => {
    const value = Number(newValue);
    if (type === "soc" && value > 100) {
      alert("SOC value cannot be over 100");
      return;
    }
    onSave(value);
    setIsOpen(false); // Close the dialog after saving
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="icon"
          className="border-none hover:shadow-none"
          onClick={() => setIsOpen(true)}
        >
          <PencilIcon size={15} className="text-primary" />
        </Button>
      </DialogTrigger>
      <DialogContent className="w-4/5">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center space-x-2">
          <div className="grid flex-1 gap-2">
            <Label htmlFor="input" className="sr-only">
              {label}
            </Label>
            <Input
              id="input"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              type="number"
              max={type === "soc" ? 100 : undefined}
              min={type === "soc" ? 0 : undefined}
              variant="primary"
            />
          </div>
          {unit && <span>{unit}</span>}
        </div>
        <DialogFooter className="sm:justify-start">
          <Button variant="primary" size="lg" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
